import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Users, Mail, Phone, School, Search, ChevronDown, ChevronUp,
  Clock, CheckCircle, AlertCircle, XCircle, Calendar, UserCheck,
  BarChart3, GraduationCap, ArrowUpRight, Filter, Send, Zap,
  Flame, TrendingUp, Snowflake, ArrowDownUp, Trash2, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getToken = () => localStorage.getItem('mosaic_session_token');

const STATUS_CONFIG = {
  new: { label: 'New', color: 'bg-blue-100 text-blue-800 border-blue-300', icon: AlertCircle },
  contacted: { label: 'Contacted', color: 'bg-amber-100 text-amber-800 border-amber-300', icon: Mail },
  scheduled: { label: 'Scheduled', color: 'bg-purple-100 text-purple-800 border-purple-300', icon: Calendar },
  won: { label: 'Won', color: 'bg-green-100 text-green-800 border-green-300', icon: CheckCircle },
  lost: { label: 'Lost', color: 'bg-gray-100 text-gray-600 border-gray-300', icon: XCircle },
  // Legacy status - treat as 'new'
  pending: { label: 'Pending', color: 'bg-blue-100 text-blue-800 border-blue-300', icon: AlertCircle },
};

// Helper to get status config with fallback
const getStatusConfig = (status) => STATUS_CONFIG[status] || STATUS_CONFIG.new;

const STATUSES = ['new', 'contacted', 'scheduled', 'won', 'lost'];

const TIER_CONFIG = {
  hot: { label: 'Hot', icon: Flame, color: 'bg-red-100 text-red-700 border-red-300', accent: 'text-red-600' },
  warm: { label: 'Warm', icon: TrendingUp, color: 'bg-amber-100 text-amber-700 border-amber-300', accent: 'text-amber-600' },
  cold: { label: 'Cold', icon: Snowflake, color: 'bg-sky-100 text-sky-700 border-sky-300', accent: 'text-sky-600' },
};

const DemoLeadsTab = () => {
  const [leads, setLeads] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [tierFilter, setTierFilter] = useState('all');
  const [sortBy, setSortBy] = useState('date'); // 'date' | 'score'
  const [expandedId, setExpandedId] = useState(null);
  const [dripProcessing, setDripProcessing] = useState(false);
  const [dripStatus, setDripStatus] = useState(null);

  // Bulk-selection + bulk-delete state (same pattern as Quotes tab v6.28.7)
  const [selected, setSelected] = useState(() => new Set());  // set of "source::id"
  const [showBulkDelete, setShowBulkDelete] = useState(false);
  const [bulkPassword, setBulkPassword] = useState('');
  const [bulkReason, setBulkReason] = useState('');
  const [bulkConfirmText, setBulkConfirmText] = useState('');
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const keyOf = (item) => `${item._source}::${item.id || item.email + (item.created_at || '')}`;

  const fetchData = async () => {
    setLoading(true);
    try {
      const [leadsRes, reqsRes, dripRes] = await Promise.all([
        axios.get(`${API}/demo-leads`),
        axios.get(`${API}/demo-requests`),
        axios.get(`${API}/demo-leads/drip-status`).catch(() => ({ data: null })),
      ]);
      setLeads(leadsRes.data.leads || []);
      setRequests(reqsRes.data.requests || []);
      if (dripRes.data) setDripStatus(dripRes.data.summary);
    } catch (err) {
      console.error('Error fetching leads:', err);
    } finally {
      setLoading(false);
    }
  };

  const processDrips = async () => {
    setDripProcessing(true);
    try {
      const res = await axios.post(`${API}/demo-leads/process-drips`);
      toast.success(`Drip processing complete: ${res.data.emails_sent} email(s) sent`);
      fetchData();
    } catch {
      toast.error('Failed to process drip emails');
    } finally {
      setDripProcessing(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  // Unify both sources into one list
  const allItems = [
    ...leads.map((l) => ({ ...l, _source: 'demo_exam', _type: 'Trial Exam' })),
    ...requests.map((r) => ({
      ...r,
      _source: 'demo_request',
      _type: 'Demo Request',
      name: r.name || r.full_name || '',
      organization: r.organization || r.school_name || '',
      grades_requested: r.grade_bands || r.grades_requested || [],
    })),
  ].sort((a, b) => {
    if (sortBy === 'score') return (b.lead_score || 0) - (a.lead_score || 0);
    return (b.created_at || b.submitted_at || '').localeCompare(a.created_at || a.submitted_at || '');
  });

  // Filters
  const filtered = allItems.filter((item) => {
    if (statusFilter !== 'all' && (item.status || 'new') !== statusFilter) return false;
    if (sourceFilter !== 'all' && item._source !== sourceFilter) return false;
    if (tierFilter !== 'all' && (item.lead_tier || 'cold') !== tierFilter) return false;
    if (search) {
      const s = search.toLowerCase();
      return (
        (item.name || '').toLowerCase().includes(s) ||
        (item.email || '').toLowerCase().includes(s) ||
        (item.organization || '').toLowerCase().includes(s)
      );
    }
    return true;
  });

  // Stats
  const countByStatus = (status) => allItems.filter((i) => (i.status || 'new') === status).length;
  const trialCount = allItems.filter((i) => i._source === 'demo_exam').length;
  const requestCount = allItems.filter((i) => i._source === 'demo_request').length;

  const updateStatus = async (item, newStatus) => {
    const endpoint = item._source === 'demo_exam'
      ? `${API}/demo-leads/${item.id}/status`
      : `${API}/demo-requests/${item.id}/status`;
    try {
      await axios.patch(endpoint, { status: newStatus });
      toast.success(`Status updated to ${STATUS_CONFIG[newStatus].label}`);
      fetchData();
    } catch {
      toast.error('Failed to update status');
    }
  };

  // v6.32.211 (FU-009) — Send a personalized demo link to a lead.
  // Only meaningful for demo_request rows (not demo_exam trial users who already
  // tried it). destination: 'demo' (public landing) or 'try-demo' (interactive exam).
  const sendDemoLink = async (item, destination = 'demo') => {
    try {
      const res = await axios.post(
        `${API}/demo-requests/${item.id}/send-demo-link`,
        { destination },
      );
      if (res.data?.sent) {
        toast.success(`Demo link sent to ${item.email}`);
      } else {
        toast.warning('Lead row updated, but email send returned non-OK (check backend logs)');
      }
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send demo link');
    }
  };

  // v6.32.217 — Archive a demo-request lead (default reason: 'spam').
  // Soft-archive: row stays in db so funnel counts don't drift, but the default
  // CRM list hides it. /unarchive flips back. Only meaningful for demo_request
  // source; demo_exam trial-users go through the bulk-delete flow above.
  const archiveLead = async (item) => {
    if (item._source !== 'demo_request') return;
    const ok = window.confirm(
      `Archive lead "${item.email}" as spam?\n\n` +
      `The row stays in the database for funnel analytics but is hidden from this list. ` +
      `You can restore it later via "Show archived".`
    );
    if (!ok) return;
    try {
      await axios.post(`${API}/demo-requests/${item.id}/archive`, { reason: 'spam' });
      toast.success(`Archived ${item.email}`);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to archive lead');
    }
  };

  // ── Suspect detection (same heuristic as v6.28.7 Quotes tab) ──
  const SPAM_REGEX = /\b(test|demo|sample|spam|qa|dummy|dev|fake|asdf|xxx|aaaa|bbbb|foo|bar)\b/i;
  const isSuspectLead = useCallback((item) => {
    // Demo-request leads legitimately say "demo" in their free-text fields,
    // so we ONLY flag them via the explicit throwaway tokens — not plain "demo".
    const tight = /\b(test|spam|qa|dummy|dev|fake|asdf|xxx|aaaa|bbbb|foo|bar)\b/i;
    const regex = item._source === 'demo_request' ? tight : SPAM_REGEX;
    const hay = [
      item.name, item.full_name, item.email,
      item.organization, item.school_name,
      item.title, item.notes, item.district,
    ].filter(Boolean).join(' | ');
    return regex.test(hay);
  }, []);
  const suspectCount = useMemo(() => filtered.filter(isSuspectLead).length, [filtered, isSuspectLead]);

  // ── Selection helpers ──
  const toggleOne = (item) => {
    const k = keyOf(item);
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });
  };
  const toggleAllVisible = () => {
    const keys = filtered.map(keyOf);
    const allSel = keys.length > 0 && keys.every(k => selected.has(k));
    setSelected(prev => {
      const next = new Set(prev);
      if (allSel) keys.forEach(k => next.delete(k));
      else keys.forEach(k => next.add(k));
      return next;
    });
  };
  const selectSuspects = () => {
    const keys = filtered.filter(isSuspectLead).map(keyOf);
    setSelected(prev => {
      const next = new Set(prev);
      keys.forEach(k => next.add(k));
      return next;
    });
  };
  const clearSelection = () => setSelected(new Set());

  const submitBulkDelete = async () => {
    if (selected.size === 0) return;
    if (bulkConfirmText.trim().toUpperCase() !== 'DELETE') return;
    // Build the payload — resolve each selected key back to an item
    const byKey = new Map(allItems.map(i => [keyOf(i), i]));
    const items = Array.from(selected)
      .map(k => byKey.get(k))
      .filter(Boolean)
      .map(i => ({ id: i.id, source: i._source }));
    setBulkDeleting(true);
    try {
      const res = await axios.post(
        `${API}/demo-leads/bulk-delete`,
        { items, password: bulkPassword, reason: bulkReason },
        { headers: { Authorization: `Bearer ${getToken()}` } },
      );
      const { deleted_count = 0, skipped = [] } = res.data || {};
      let msg = `Deleted ${deleted_count} lead${deleted_count === 1 ? '' : 's'}.`;
      if (skipped.length) msg += `\nSkipped ${skipped.length} (not found or unknown source).`;
      toast.success(msg);
      clearSelection();
      setShowBulkDelete(false);
      setBulkPassword('');
      setBulkReason('');
      setBulkConfirmText('');
      fetchData();
    } catch (err) {
      toast.error('Bulk delete failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setBulkDeleting(false);
    }
  };

  const formatDate = (d) => {
    if (!d) return '—';
    try {
      return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
    } catch { return d; }
  };

  if (loading) return <div className="py-16 text-center"><div className="loading-spinner mx-auto" /></div>;

  return (
    <div className="space-y-6" data-testid="demo-leads-tab">
      {/* Pipeline Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {STATUSES.map((s) => {
          const cfg = STATUS_CONFIG[s];
          const Icon = cfg.icon;
          const count = countByStatus(s);
          return (
            <button
              key={s}
              onClick={() => setStatusFilter(statusFilter === s ? 'all' : s)}
              className={`p-4 border-2 text-left transition-all ${
                statusFilter === s ? 'border-[#002FA7] ring-1 ring-[#002FA7]' : 'border-gray-200 hover:border-gray-300'
              }`}
              data-testid={`pipeline-${s}`}
            >
              <div className="flex items-center gap-2 mb-1">
                <Icon size={14} className={cfg.color.split(' ')[1]} />
                <span className="text-xs font-semibold uppercase">{cfg.label}</span>
              </div>
              <div className="text-2xl font-black" style={{ fontFamily: 'Outfit, sans-serif' }}>{count}</div>
            </button>
          );
        })}
      </div>

      {/* Drip Email Status */}
      <div className="bg-white border-2 border-gray-200 p-4 flex flex-wrap items-center gap-4" data-testid="drip-status-card">
        <div className="flex items-center gap-2">
          <Zap size={18} className="text-amber-500" />
          <span className="font-bold text-sm" style={{ fontFamily: 'Outfit, sans-serif' }}>Email Drip Sequence</span>
        </div>
        {dripStatus && (
          <div className="flex gap-3 text-xs">
            <span className="px-2 py-1 bg-blue-50 text-blue-700 border border-blue-200 font-semibold">
              Pending: {dripStatus.pending}
            </span>
            <span className="px-2 py-1 bg-amber-50 text-amber-700 border border-amber-200 font-semibold">
              Drip 1 (48h): {dripStatus.drip_1_sent}
            </span>
            <span className="px-2 py-1 bg-purple-50 text-purple-700 border border-purple-200 font-semibold">
              Drip 2 (5d): {dripStatus.drip_2_sent}
            </span>
            <span className="px-2 py-1 bg-red-50 text-red-700 border border-red-200 font-semibold">
              Drip 3 (10d): {dripStatus.drip_3_sent}
            </span>
          </div>
        )}
        <Button
          onClick={processDrips}
          disabled={dripProcessing}
          variant="outline"
          className="ml-auto btn-sharp h-9 text-xs"
          data-testid="process-drips-btn"
        >
          {dripProcessing ? (
            <><div className="loading-spinner w-3 h-3 mr-1 border-gray-400 border-t-transparent" /> Processing...</>
          ) : (
            <><Send size={12} className="mr-1" /> Process Drips Now</>
          )}
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            placeholder="Search name, email, or organization..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 h-10 border-2 border-gray-300 rounded-none"
            data-testid="leads-search"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter size={14} className="text-gray-400" />
          <button
            onClick={() => setSourceFilter('all')}
            className={`px-3 py-1.5 text-xs font-semibold border-2 transition-colors ${
              sourceFilter === 'all' ? 'border-[#002FA7] bg-blue-50 text-[#002FA7]' : 'border-gray-200 text-gray-600'
            }`}
            data-testid="filter-all"
          >
            All ({allItems.length})
          </button>
          <button
            onClick={() => setSourceFilter('demo_exam')}
            className={`px-3 py-1.5 text-xs font-semibold border-2 transition-colors ${
              sourceFilter === 'demo_exam' ? 'border-[#002FA7] bg-blue-50 text-[#002FA7]' : 'border-gray-200 text-gray-600'
            }`}
            data-testid="filter-trials"
          >
            Trial Exams ({trialCount})
          </button>
          <button
            onClick={() => setSourceFilter('demo_request')}
            className={`px-3 py-1.5 text-xs font-semibold border-2 transition-colors ${
              sourceFilter === 'demo_request' ? 'border-[#002FA7] bg-blue-50 text-[#002FA7]' : 'border-gray-200 text-gray-600'
            }`}
            data-testid="filter-requests"
          >
            Demo Requests ({requestCount})
          </button>
        </div>
        {/* Lead Score Tier Filters */}
        <div className="flex items-center gap-2">
          {['hot', 'warm', 'cold'].map((t) => {
            const tc = TIER_CONFIG[t];
            const TierIcon = tc.icon;
            const count = allItems.filter((i) => (i.lead_tier || 'cold') === t).length;
            return (
              <button
                key={t}
                onClick={() => setTierFilter(tierFilter === t ? 'all' : t)}
                className={`px-3 py-1.5 text-xs font-semibold border-2 transition-colors flex items-center gap-1 ${
                  tierFilter === t ? `${tc.color}` : 'border-gray-200 text-gray-600'
                }`}
                data-testid={`filter-tier-${t}`}
              >
                <TierIcon size={12} /> {tc.label} ({count})
              </button>
            );
          })}
        </div>
        {/* Sort Toggle */}
        <button
          onClick={() => setSortBy(sortBy === 'date' ? 'score' : 'date')}
          className="px-3 py-1.5 text-xs font-semibold border-2 border-gray-200 text-gray-600 hover:border-gray-400 flex items-center gap-1 transition-colors"
          data-testid="sort-toggle"
        >
          <ArrowDownUp size={12} />
          {sortBy === 'score' ? 'By Score' : 'By Date'}
        </button>
        <span className="text-xs text-gray-400 ml-auto">{filtered.length} results</span>
      </div>

      {/* Bulk-selection toolbar (v6.28.8) */}
      <div className="flex items-center gap-2 flex-wrap text-xs">
        <label className="flex items-center gap-1.5 cursor-pointer px-2 py-1 border border-gray-200 hover:border-gray-400">
          <input
            type="checkbox"
            aria-label="Select all visible leads"
            checked={filtered.length > 0 && filtered.every(i => selected.has(keyOf(i)))}
            onChange={toggleAllVisible}
            data-testid="select-all-leads"
          />
          <span className="text-gray-600">Select all {filtered.length}</span>
        </label>
        {suspectCount > 0 && (
          <Button
            size="sm" variant="outline"
            onClick={selectSuspects}
            className="text-amber-700 border-amber-300 bg-amber-50 hover:bg-amber-100 h-auto py-1"
            title="Select leads whose name / email / org contain TEST, DEMO, SAMPLE, SPAM, QA, DUMMY, DEV, FAKE, asdf, etc."
            data-testid="select-suspects-leads-btn"
          >
            <AlertTriangle size={12} className="mr-1" /> Select {suspectCount} TEST/SPAM
          </Button>
        )}
        {selected.size > 0 && (
          <>
            <span className="text-gray-600" data-testid="leads-selection-count"><b>{selected.size}</b> selected</span>
            <Button size="sm" variant="outline" onClick={clearSelection} className="h-auto py-1" data-testid="clear-leads-selection-btn">
              Clear
            </Button>
            <Button
              size="sm"
              onClick={() => setShowBulkDelete(true)}
              className="bg-red-600 hover:bg-red-700 text-white h-auto py-1 gap-1"
              data-testid="open-leads-bulk-delete-btn"
            >
              <Trash2 size={12} /> Delete {selected.size}
            </Button>
          </>
        )}
      </div>

      {/* Leads Table */}
      {filtered.length === 0 ? (
        <div className="text-center py-16 border-2 border-dashed border-gray-200">
          <Users size={40} className="mx-auto text-gray-300 mb-3" />
          <p className="text-gray-500 font-semibold">No leads found</p>
          <p className="text-sm text-gray-400">Share /try-demo or /request-demo to start capturing leads.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((item) => {
            const status = item.status || 'new';
            const cfg = getStatusConfig(status);
            const StatusIcon = cfg.icon;
            const isExpanded = expandedId === item.id;
            const grades = item.grades_requested || [];

            return (
              <div key={item.id || item.email + item.created_at} className={`border-2 bg-white ${selected.has(keyOf(item)) ? 'border-[#002FA7]' : isSuspectLead(item) ? 'border-amber-300 bg-amber-50/30' : 'border-gray-200'}`} data-testid={`lead-row-${item.id}`}>
                {/* Summary Row */}
                <div className="flex items-stretch">
                  {/* Selection checkbox column */}
                  <div
                    className="flex items-center justify-center px-3 border-r border-gray-200 bg-gray-50/50 cursor-pointer"
                    onClick={(e) => { e.stopPropagation(); toggleOne(item); }}
                  >
                    <input
                      type="checkbox"
                      aria-label={`Select ${item.email || item.name || item.id}`}
                      checked={selected.has(keyOf(item))}
                      onChange={() => toggleOne(item)}
                      onClick={(e) => e.stopPropagation()}
                      data-testid={`select-lead-${item.id}`}
                    />
                  </div>
                  <button
                    onClick={() => setExpandedId(isExpanded ? null : item.id)}
                    className="flex-1 text-left p-4 flex items-center gap-4 hover:bg-gray-50 transition-colors"
                    data-testid={`lead-toggle-${item.id}`}
                  >
                  <div className={`px-2 py-1 text-xs font-bold border ${cfg.color} flex items-center gap-1 flex-shrink-0`}>
                    <StatusIcon size={12} />
                    {cfg.label}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-sm truncate">{item.name || '—'}</span>
                      <span className="text-xs text-gray-400">|</span>
                      <span className="text-sm text-gray-600 truncate">{item.organization || '—'}</span>
                      {isSuspectLead(item) && (
                        <span className="ml-1 text-[9px] px-1 py-0.5 bg-amber-200 text-amber-900 font-bold border border-amber-400 uppercase tracking-wider">Test</span>
                      )}
                      {/* v6.32.221 — NYC ELD Pilot intel badge. Surfaces
                          when the lead's organization text references a
                          piloted NYCPS district (D2/6/7/18 = Reach Higher,
                          D10/21/24/25 = English 3D). Internal-only intel. */}
                      {item.pilot_hint && (
                        <span
                          className="ml-1 text-[9px] px-1.5 py-0.5 bg-indigo-100 text-indigo-800 font-bold border border-indigo-300 uppercase tracking-wider"
                          title={`District ${item.pilot_hint.district_number} pilot · ${item.pilot_hint.publisher}`}
                          data-testid={`pilot-hint-badge-${item.id}`}
                        >
                          D{item.pilot_hint.district_number} · {item.pilot_hint.curriculum_name}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-400">
                      <span className="flex items-center gap-1"><Mail size={10} /> {item.email}</span>
                      <span className="flex items-center gap-1"><Clock size={10} /> {formatDate(item.created_at || item.submitted_at)}</span>
                    </div>
                  </div>
                  {grades.length > 0 && (
                    <div className="flex gap-1 flex-shrink-0">
                      {grades.map((g) => (
                        <span key={g} className="px-2 py-0.5 bg-blue-50 text-blue-700 text-xs font-bold border border-blue-200">{g}</span>
                      ))}
                    </div>
                  )}
                  {item.demo_wida_result && (
                    <div className="flex items-center gap-1 flex-shrink-0 px-2 py-1 bg-emerald-50 text-emerald-700 text-xs font-bold border border-emerald-200" data-testid="lead-wida-score">
                      <BarChart3 size={12} /> WIDA {item.demo_wida_result}
                    </div>
                  )}
                  {/* Lead Score Badge */}
                  {item.lead_tier && (() => {
                    const tc = TIER_CONFIG[item.lead_tier] || TIER_CONFIG.cold;
                    const TierIcon = tc.icon;
                    return (
                      <div className={`flex items-center gap-1 flex-shrink-0 px-2 py-1 text-xs font-bold border ${tc.color}`} data-testid={`lead-score-badge-${item.id}`}>
                        <TierIcon size={12} /> {item.lead_score}
                      </div>
                    );
                  })()}
                  <span className={`px-2 py-0.5 text-xs font-semibold ${
                    item._source === 'demo_exam' ? 'bg-emerald-50 text-emerald-700' : 'bg-violet-50 text-violet-700'
                  }`}>
                    {item._type}
                  </span>
                  {isExpanded ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
                </button>
                </div>

                {/* Expanded Detail */}
                {isExpanded && (
                  <div className="border-t border-gray-200 p-5 bg-gray-50 space-y-4" data-testid={`lead-detail-${item.id}`}>
                    <div className="grid sm:grid-cols-3 gap-4 text-sm">
                      <div>
                        <p className="text-xs text-gray-400 font-semibold mb-1">CONTACT</p>
                        <p className="font-semibold">{item.name}</p>
                        <p className="text-gray-600">{item.title || '—'}</p>
                        <p className="flex items-center gap-1 mt-1"><Mail size={12} className="text-gray-400" /> {item.email}</p>
                        {item.phone && <p className="flex items-center gap-1"><Phone size={12} className="text-gray-400" /> {item.phone}</p>}
                      </div>
                      <div>
                        <p className="text-xs text-gray-400 font-semibold mb-1">ORGANIZATION</p>
                        <p className="font-semibold flex items-center gap-1"><School size={14} /> {item.organization}</p>
                        {item.district && <p className="text-gray-600 mt-1">District: {item.district}</p>}
                        {item.estimated_ell_students && <p className="text-gray-600">ELL Students: {item.estimated_ell_students}</p>}
                      </div>
                      <div>
                        <p className="text-xs text-gray-400 font-semibold mb-1">DEMO INFO</p>
                        {item.demo_grade_tried && <p>Tried: Grade {item.demo_grade_tried}</p>}
                        {item.demo_wida_result && <p className="font-semibold text-emerald-700">Proficiency Score: {item.demo_wida_result}</p>}
                        {grades.length > 0 && (
                          <p className="mt-1">
                            <span className="text-xs text-gray-400">Grades Requested:</span>{' '}
                            <span className="font-semibold text-[#002FA7]">{grades.join(', ')}</span>
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Lead Score Breakdown */}
                    {item.lead_score !== undefined && (
                      <div className="border border-gray-200 bg-white p-3" data-testid={`lead-score-detail-${item.id}`}>
                        <p className="text-xs text-gray-400 font-semibold mb-2 flex items-center gap-1">
                          {(() => { const tc = TIER_CONFIG[item.lead_tier] || TIER_CONFIG.cold; const TI = tc.icon; return <TI size={12} />; })()}
                          LEAD SCORE
                        </p>
                        <div className="flex items-center gap-3">
                          <div className={`px-3 py-2 font-black text-lg ${(TIER_CONFIG[item.lead_tier] || TIER_CONFIG.cold).color}`} style={{ fontFamily: 'Outfit, sans-serif' }}>
                            {item.lead_score}/100
                          </div>
                          <div className={`px-3 py-1 text-sm font-bold ${(TIER_CONFIG[item.lead_tier] || TIER_CONFIG.cold).color}`}>
                            {(TIER_CONFIG[item.lead_tier] || TIER_CONFIG.cold).label} Lead
                          </div>
                          <div className="flex-1 bg-gray-200 h-2 overflow-hidden">
                            <div className={`h-full transition-all ${
                              item.lead_tier === 'hot' ? 'bg-red-500' : item.lead_tier === 'warm' ? 'bg-amber-500' : 'bg-sky-400'
                            }`} style={{ width: `${item.lead_score}%` }} />
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2 mt-2 text-xs text-gray-500">
                          {item.demo_session_id && <span className="px-2 py-0.5 bg-green-50 text-green-700 border border-green-200">+25 Demo Completed</span>}
                          {(item.is_decision_maker || item.decision_maker_email) && <span className="px-2 py-0.5 bg-green-50 text-green-700 border border-green-200">+{item.is_decision_maker ? '25' : '20'} Decision Maker</span>}
                          {item.estimated_ell_students && <span className="px-2 py-0.5 bg-green-50 text-green-700 border border-green-200">+ELL Count</span>}
                          {grades.length > 0 && <span className="px-2 py-0.5 bg-green-50 text-green-700 border border-green-200">+{grades.length} Grade(s)</span>}
                          {item.phone && <span className="px-2 py-0.5 bg-green-50 text-green-700 border border-green-200">+5 Phone</span>}
                          {item.message && <span className="px-2 py-0.5 bg-green-50 text-green-700 border border-green-200">+5 Message</span>}
                          {item.district && <span className="px-2 py-0.5 bg-green-50 text-green-700 border border-green-200">+5 District</span>}
                        </div>
                      </div>
                    )}

                    {/* Decision Maker */}
                    {item.is_decision_maker !== undefined && (
                      <div className="border border-gray-200 bg-white p-3">
                        <p className="text-xs text-gray-400 font-semibold mb-1 flex items-center gap-1">
                          <UserCheck size={12} /> DECISION MAKER
                        </p>
                        {item.is_decision_maker ? (
                          <p className="text-sm font-semibold text-green-700">This contact IS the decision maker</p>
                        ) : (
                          <div className="text-sm">
                            <p className="font-semibold">{item.decision_maker_name || '—'}</p>
                            <p className="text-gray-600">{item.decision_maker_title || '—'}</p>
                            {item.decision_maker_email && (
                              <p className="flex items-center gap-1 mt-0.5">
                                <Mail size={12} className="text-gray-400" /> {item.decision_maker_email}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Message */}
                    {item.message && (
                      <div className="border-l-3 border-[#002FA7] bg-white p-3 text-sm" style={{ borderLeftWidth: 3 }}>
                        <p className="text-xs text-gray-400 font-semibold mb-1">MESSAGE</p>
                        <p className="text-gray-700">{item.message}</p>
                      </div>
                    )}

                    {/* Drip Email Status */}
                    {item._source === 'demo_exam' && (
                      <div className="border border-gray-200 bg-white p-3" data-testid={`drip-info-${item.id}`}>
                        <p className="text-xs text-gray-400 font-semibold mb-2 flex items-center gap-1">
                          <Zap size={12} /> DRIP EMAILS
                        </p>
                        <div className="flex gap-2">
                          {[
                            { id: 'drip_1', label: '48h Follow-up', hours: 48 },
                            { id: 'drip_2', label: '5-Day Case Study', hours: 120 },
                            { id: 'drip_3', label: '10-Day Final', hours: 240 },
                          ].map((d) => {
                            const sent = (item.drips_sent || []).includes(d.id);
                            return (
                              <span key={d.id} className={`px-2 py-1 text-xs font-semibold border ${
                                sent ? 'bg-green-50 text-green-700 border-green-200' : 'bg-gray-50 text-gray-400 border-gray-200'
                              }`}>
                                {sent ? <CheckCircle size={10} className="inline mr-1" /> : <Clock size={10} className="inline mr-1" />}
                                {d.label}
                              </span>
                            );
                          })}
                        </div>
                        {item.last_drip_at && (
                          <p className="text-xs text-gray-400 mt-1">Last drip: {formatDate(item.last_drip_at)}</p>
                        )}
                      </div>
                    )}

                    {/* Status Actions */}
                    <div className="flex items-center gap-2 pt-2 flex-wrap">
                      <span className="text-xs text-gray-400 mr-2">Update status:</span>
                      {STATUSES.map((s) => {
                        const sc = STATUS_CONFIG[s];
                        return (
                          <button
                            key={s}
                            onClick={() => updateStatus(item, s)}
                            disabled={status === s}
                            className={`px-3 py-1.5 text-xs font-semibold border transition-colors ${
                              status === s
                                ? `${sc.color} cursor-default`
                                : 'border-gray-200 text-gray-500 hover:border-gray-400'
                            }`}
                            data-testid={`status-btn-${s}-${item.id}`}
                          >
                            {sc.label}
                          </button>
                        );
                      })}

                      {/* v6.32.211 (FU-009): Send Demo Link buttons — only on demo_request rows */}
                      {item._source !== 'demo_exam' && (
                        <>
                          <span className="text-xs text-gray-400 ml-3 mr-1">|</span>
                          <button
                            onClick={() => sendDemoLink(item, 'demo')}
                            className="px-3 py-1.5 text-xs font-semibold border border-blue-300 text-blue-700 hover:bg-blue-50 inline-flex items-center gap-1"
                            title="Email this lead a link to the public /demo landing"
                            data-testid={`send-demo-link-demo-${item.id}`}
                          >
                            <Send size={11} /> Send /demo link
                          </button>
                          <button
                            onClick={() => sendDemoLink(item, 'try-demo')}
                            className="px-3 py-1.5 text-xs font-semibold border border-emerald-300 text-emerald-700 hover:bg-emerald-50 inline-flex items-center gap-1"
                            title="Email this lead a link to the interactive /try-demo exam"
                            data-testid={`send-demo-link-trydemo-${item.id}`}
                          >
                            <Send size={11} /> Send /try-demo link
                          </button>
                          {item.demo_link_sent_at && (
                            <span className="text-xs text-gray-500 ml-1" data-testid={`demo-link-sent-${item.id}`}>
                              Sent {item.demo_link_send_count || 1}× · last {new Date(item.demo_link_sent_at).toLocaleDateString()}
                            </span>
                          )}
                          {/* v6.32.217: Archive (spam) — hide from default list, keep in DB for analytics */}
                          <button
                            onClick={() => archiveLead(item)}
                            className="px-3 py-1.5 text-xs font-semibold border border-rose-300 text-rose-700 hover:bg-rose-50 inline-flex items-center gap-1"
                            title="Archive as spam — hides from this list but keeps the row in DB for funnel analytics"
                            data-testid={`archive-spam-${item.id}`}
                          >
                            🚫 Mark spam
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Bulk Delete Leads Modal (password-gated, v6.28.8) ── */}
      {showBulkDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="leads-bulk-delete-dialog">
          <div className="bg-white border-2 border-red-300 shadow-2xl max-w-lg w-full p-6 space-y-4">
            <div className="flex items-center gap-2 text-red-700">
              <AlertTriangle size={20} />
              <h3 className="text-lg font-bold">Delete {selected.size} Lead{selected.size === 1 ? '' : 's'}</h3>
            </div>

            <div className="bg-red-50 border border-red-200 p-3 text-xs text-red-800 leading-relaxed">
              <div className="font-semibold mb-1">This cannot be undone.</div>
              The {selected.size} selected lead{selected.size === 1 ? '' : 's'} will be permanently removed
              from the demo leads / demo requests collections. An audit row is written for each deletion.
            </div>

            <div>
              <label className="text-xs font-semibold block mb-1">Your Super Admin password *</label>
              <Input
                type="password"
                value={bulkPassword}
                onChange={(e) => setBulkPassword(e.target.value)}
                placeholder="Re-enter password to confirm"
                autoFocus
                data-testid="leads-bulk-delete-password"
              />
            </div>

            <div>
              <label className="text-xs font-semibold block mb-1">Reason (optional, logged for audit)</label>
              <Input
                value={bulkReason}
                onChange={(e) => setBulkReason(e.target.value)}
                placeholder="e.g. TEST / SPAM bot cleanup"
                data-testid="leads-bulk-delete-reason"
              />
            </div>

            <div>
              <label className="text-xs font-semibold block mb-1">
                Type <span className="font-mono font-bold text-red-700">DELETE</span> to confirm *
              </label>
              <Input
                value={bulkConfirmText}
                onChange={(e) => setBulkConfirmText(e.target.value)}
                placeholder="DELETE"
                data-testid="leads-bulk-delete-confirm-text"
              />
            </div>

            <div className="flex items-center justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowBulkDelete(false)} disabled={bulkDeleting}>Cancel</Button>
              <Button
                onClick={submitBulkDelete}
                disabled={
                  bulkDeleting ||
                  !bulkPassword ||
                  bulkConfirmText.trim().toUpperCase() !== 'DELETE'
                }
                className="bg-red-600 hover:bg-red-700 text-white gap-1.5"
                data-testid="leads-bulk-delete-submit"
              >
                <Trash2 size={14} /> {bulkDeleting ? 'Deleting…' : `Delete ${selected.size}`}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DemoLeadsTab;
